import React from 'react';
import { Helmet } from 'react-helmet';
import { useSeo } from '../../context/SeoContext';

/**
 * Componente SEO aprimorado para gerenciar meta tags e schemas estruturados
 * @param {Object} props - Propriedades adicionais para sobrescrever dados do contexto (opcional)
 */
const SEO = (props) => {
  const { seoData } = useSeo();
  
  // Mescla as props com os dados do contexto (as props têm prioridade)
  const {
    title = seoData.title,
    description = seoData.description,
    keywords = seoData.keywords,
    canonicalUrl = seoData.canonicalUrl,
    structuredData = seoData.structuredData,
    image = seoData.image || '/images/calculator-social.jpg',
    locale = 'pt_BR',
    type = 'website',
    twitterCard = 'summary_large_image',
    publishedTime = null,
    modifiedTime = null,
    articleSection = null,
    author = null
  } = props;

  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    'name': 'Calculadora de Juros Compostos',
    'description': description,
    'applicationCategory': 'FinanceApplication',
    'offers': {
      '@type': 'Offer',
      'price': '0',
      'priceCurrency': 'BRL'
    }
  };

  const mergedStructuredData = { ...defaultStructuredData, ...structuredData };
  
  return (
    <Helmet>
      {/* Título e meta tags básicas */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={Array.isArray(keywords) ? keywords.join(', ') : keywords} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="robots" content="index, follow" />
      
      {/* Link canônico */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Meta tags para Open Graph (Facebook) */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content={locale} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      {image && <meta property="og:image" content={image} />}
      {image && <meta property="og:image:alt" content={title} />}
      
      {/* Meta tags para Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}
      
      {/* Metadados para artigos (quando aplicável) */}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {articleSection && <meta property="article:section" content={articleSection} />}
      {author && <meta property="article:author" content={author} />}
      
      {/* Dados estruturados */}
      <script type="application/ld+json">
        {JSON.stringify(mergedStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
